/**
 * Admin Payment Management Page
 * View and manage all payment transactions
 */

import React, { useState } from 'react';
import { Navbar, Footer } from '../../components';
import { formatDate } from '../../utils/dateUtils';
import SEO from '../../components/common/SEO';

// Sample payments data
const paymentsData = [
  {
    id: 'TXN-001',
    student: { name: 'Alice Johnson', email: 'alice@example.com', avatar: 'https://i.pravatar.cc/40?img=1' },
    course: 'Complete React.js Masterclass',
    teacher: 'John Smith',
    amount: 49.99,
    status: 'completed',
    method: 'Credit Card',
    date: '2026-01-13',
  },
  {
    id: 'TXN-002',
    student: { name: 'Bob Smith', email: 'bob@example.com', avatar: 'https://i.pravatar.cc/40?img=2' },
    course: 'Python for Data Science',
    teacher: 'Michael Chen',
    amount: 0,
    status: 'completed',
    method: 'Free Enrollment',
    date: '2026-01-12',
  },
  {
    id: 'TXN-003',
    student: { name: 'Carol Davis', email: 'carol@example.com', avatar: 'https://i.pravatar.cc/40?img=3' },
    course: 'Machine Learning A-Z',
    teacher: 'David Wilson',
    amount: 79.99,
    status: 'completed',
    method: 'PayPal',
    date: '2026-01-12',
  },
  {
    id: 'TXN-004',
    student: { name: 'David Wilson', email: 'david@example.com', avatar: 'https://i.pravatar.cc/40?img=4' },
    course: 'Java Programming Bootcamp',
    teacher: 'Sarah Johnson',
    amount: 59.99,
    status: 'refunded',
    method: 'Credit Card',
    date: '2026-01-11',
  },
  {
    id: 'TXN-005',
    student: { name: 'Emma Brown', email: 'emma@example.com', avatar: 'https://i.pravatar.cc/40?img=5' },
    course: 'SQL Database Mastery',
    teacher: 'Emily Davis',
    amount: 39.99,
    status: 'completed',
    method: 'Stripe',
    date: '2026-01-10',
  },
  {
    id: 'TXN-006',
    student: { name: 'Frank Miller', email: 'frank@example.com', avatar: 'https://i.pravatar.cc/40?img=6' },
    course: 'Complete React.js Masterclass',
    teacher: 'John Smith',
    amount: 49.99,
    status: 'pending',
    method: 'Bank Transfer',
    date: '2026-01-10',
  },
];

const PaymentManagement = () => {
  const [payments] = useState(paymentsData);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredPayments = payments.filter((payment) => {
    const matchesFilter = filter === 'all' || payment.status === filter;
    const matchesSearch = payment.student.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         payment.course.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         payment.id.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const totalRevenue = payments
    .filter((p) => p.status === 'completed' && p.amount > 0)
    .reduce((sum, p) => sum + p.amount, 0);

  return (
    <div className="lh-payment-management min-h-screen bg-dark-50">
      <SEO title="Payment Management" noIndex />
      <Navbar />

      <main className="lh-payment-management__main pt-20 pb-12">
        <div className="lh-payment-management__container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="lh-payment-management__header flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div className="lh-payment-management__header-info">
              <h1 className="lh-payment-management__title text-2xl md:text-3xl font-bold text-dark-900 mb-1">
                Payment Management
              </h1>
              <p className="lh-payment-management__subtitle text-dark-500">
                Track and manage all platform transactions
              </p>
            </div>
            <div className="lh-payment-management__search relative">
              <input
                type="text"
                placeholder="Search transactions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="lh-payment-management__search-input input-field pl-10 py-2 w-64"
              />
              <svg className="lh-payment-management__search-icon w-5 h-5 text-dark-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="lh-payment-management__summary grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8">
            <div className="lh-payment-management__summary-card lh-payment-management__summary-card--revenue card p-5">
              <p className="lh-payment-management__summary-label text-sm text-dark-500 mb-1">Total Revenue</p>
              <p className="lh-payment-management__summary-value text-2xl font-bold text-dark-900">${totalRevenue.toFixed(2)}</p>
            </div>
            <div className="lh-payment-management__summary-card lh-payment-management__summary-card--completed card p-5">
              <p className="lh-payment-management__summary-label text-sm text-dark-500 mb-1">Completed</p>
              <p className="lh-payment-management__summary-value text-2xl font-bold text-green-500">
                {payments.filter((p) => p.status === 'completed').length}
              </p>
            </div>
            <div className="lh-payment-management__summary-card lh-payment-management__summary-card--pending card p-5">
              <p className="lh-payment-management__summary-label text-sm text-dark-500 mb-1">Pending</p>
              <p className="lh-payment-management__summary-value text-2xl font-bold text-yellow-500">
                {payments.filter((p) => p.status === 'pending').length}
              </p>
            </div>
            <div className="lh-payment-management__summary-card lh-payment-management__summary-card--refunded card p-5">
              <p className="lh-payment-management__summary-label text-sm text-dark-500 mb-1">Refunded</p>
              <p className="lh-payment-management__summary-value text-2xl font-bold text-red-500">
                {payments.filter((p) => p.status === 'refunded').length}
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="lh-payment-management__filters flex flex-wrap gap-3 mb-6">
            <button
              onClick={() => setFilter('all')}
              className={`lh-payment-management__filter lh-payment-management__filter--all px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                filter === 'all' ? 'lh-payment-management__filter--active bg-primary-500 text-white' : 'bg-white text-dark-600 hover:bg-dark-100'
              }`}
            >
              All Transactions
            </button>
            <button
              onClick={() => setFilter('completed')}
              className={`lh-payment-management__filter lh-payment-management__filter--completed px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                filter === 'completed' ? 'lh-payment-management__filter--active bg-green-500 text-white' : 'bg-white text-dark-600 hover:bg-dark-100'
              }`}
            >
              Completed
            </button>
            <button
              onClick={() => setFilter('pending')}
              className={`lh-payment-management__filter lh-payment-management__filter--pending px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                filter === 'pending' ? 'lh-payment-management__filter--active bg-yellow-500 text-white' : 'bg-white text-dark-600 hover:bg-dark-100'
              }`}
            >
              Pending
            </button>
            <button
              onClick={() => setFilter('refunded')}
              className={`lh-payment-management__filter lh-payment-management__filter--refunded px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                filter === 'refunded' ? 'lh-payment-management__filter--active bg-red-500 text-white' : 'bg-white text-dark-600 hover:bg-dark-100'
              }`}
            >
              Refunded
            </button>
          </div>

          {/* Payments Table */}
          <div className="lh-payment-management__table card overflow-hidden">
            <div className="lh-payment-management__desktop hidden md:block overflow-x-auto">
              <table className="lh-payment-management__table-element w-full">
                <thead className="lh-payment-management__table-head bg-dark-50">
                  <tr className="lh-payment-management__table-row">
                    <th className="lh-payment-management__table-th text-left p-4 text-sm font-semibold text-dark-600">Transaction ID</th>
                    <th className="lh-payment-management__table-th text-left p-4 text-sm font-semibold text-dark-600">Student</th>
                    <th className="lh-payment-management__table-th text-left p-4 text-sm font-semibold text-dark-600">Course</th>
                    <th className="lh-payment-management__table-th text-left p-4 text-sm font-semibold text-dark-600">Date</th>
                    <th className="lh-payment-management__table-th text-left p-4 text-sm font-semibold text-dark-600">Method</th>
                    <th className="lh-payment-management__table-th text-left p-4 text-sm font-semibold text-dark-600">Amount</th>
                    <th className="lh-payment-management__table-th text-left p-4 text-sm font-semibold text-dark-600">Status</th>
                  </tr>
                </thead>
                <tbody className="lh-payment-management__table-body divide-y divide-dark-100">
                  {filteredPayments.map((payment) => (
                    <tr key={payment.id} className="lh-payment-management__payment-row hover:bg-dark-50">
                      <td className="lh-payment-management__payment-cell lh-payment-management__payment-cell--id p-4 text-sm font-medium text-dark-900">{payment.id}</td>
                      <td className="lh-payment-management__payment-cell lh-payment-management__payment-cell--student p-4">
                        <div className="lh-payment-management__student flex items-center gap-3">
                          <img
                            src={payment.student.avatar}
                            alt={payment.student.name}
                            className="lh-payment-management__student-avatar w-8 h-8 rounded-full"
                          />
                          <div className="lh-payment-management__student-info">
                            <p className="lh-payment-management__student-name text-sm font-medium text-dark-900">{payment.student.name}</p>
                            <p className="lh-payment-management__student-email text-xs text-dark-500">{payment.student.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="lh-payment-management__payment-cell lh-payment-management__payment-cell--course p-4">
                        <p className="lh-payment-management__course-title text-sm text-dark-900">{payment.course}</p>
                        <p className="lh-payment-management__course-teacher text-xs text-dark-500">by {payment.teacher}</p>
                      </td>
                      <td className="lh-payment-management__payment-cell lh-payment-management__payment-cell--date p-4 text-sm text-dark-500">{formatDate(payment.date)}</td>
                      <td className="lh-payment-management__payment-cell lh-payment-management__payment-cell--method p-4 text-sm text-dark-600">{payment.method}</td>
                      <td className="lh-payment-management__payment-cell lh-payment-management__payment-cell--amount p-4 text-sm font-semibold text-dark-900">
                        {payment.amount > 0 ? `$${payment.amount.toFixed(2)}` : 'Free'}
                      </td>
                      <td className="lh-payment-management__payment-cell lh-payment-management__payment-cell--status p-4">
                        <span className={`lh-payment-management__status-badge lh-payment-management__status-badge--${payment.status} text-xs font-semibold px-2.5 py-1 rounded-full ${
                          payment.status === 'completed' ? 'bg-green-100 text-green-600' :
                          payment.status === 'pending' ? 'bg-yellow-100 text-yellow-600' :
                          'bg-red-100 text-red-600'
                        }`}>
                          {payment.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="lh-payment-management__mobile md:hidden divide-y divide-dark-100">
              {filteredPayments.map((payment) => (
                <div key={payment.id} className="lh-payment-management__mobile-card p-4">
                  <div className="lh-payment-management__mobile-header flex items-start justify-between mb-2">
                    <span className="lh-payment-management__mobile-id text-xs font-medium text-dark-400">{payment.id}</span>
                    <span className={`lh-payment-management__mobile-status text-xs font-semibold px-2 py-0.5 rounded-full ${
                      payment.status === 'completed' ? 'bg-green-100 text-green-600' :
                      payment.status === 'pending' ? 'bg-yellow-100 text-yellow-600' :
                      'bg-red-100 text-red-600'
                    }`}>
                      {payment.status}
                    </span>
                  </div>
                  <div className="lh-payment-management__mobile-content flex items-center gap-3 mb-2">
                    <img
                      src={payment.student.avatar}
                      alt={payment.student.name}
                      className="lh-payment-management__mobile-avatar w-10 h-10 rounded-full"
                    />
                    <div className="lh-payment-management__mobile-info flex-1 min-w-0">
                      <p className="lh-payment-management__mobile-name font-medium text-dark-900 truncate">{payment.student.name}</p>
                      <p className="lh-payment-management__mobile-course text-sm text-dark-500 truncate">{payment.course}</p>
                    </div>
                    <p className="lh-payment-management__mobile-amount font-bold text-dark-900">
                      {payment.amount > 0 ? `$${payment.amount.toFixed(2)}` : 'Free'}
                    </p>
                  </div>
                  <div className="lh-payment-management__mobile-footer flex items-center justify-between text-xs text-dark-400">
                    <span className="lh-payment-management__mobile-method">{payment.method}</span>
                    <span className="lh-payment-management__mobile-date">{formatDate(payment.date)}</span>
                  </div>
                </div>
              ))}
            </div>

            {filteredPayments.length === 0 && (
              <div className="lh-payment-management__empty p-12 text-center">
                <svg className="lh-payment-management__empty-icon w-16 h-16 text-dark-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <h3 className="lh-payment-management__empty-title text-lg font-semibold text-dark-900 mb-1">No transactions found</h3>
                <p className="lh-payment-management__empty-text text-dark-500">Try adjusting your search or filters</p>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PaymentManagement;
